import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, LifeBuoy, Loader2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { STYLES_BY_KIND, blankConfig, defaultConfig, drivetrainById, portConflicts, rcProjectName } from "@/rc/drivetrains";
import { hubReady } from "@/lib/hubReady";
import { buttonConflicts, fitAttachments } from "@/rc/attachments";
import { DrivetrainStep } from "./DrivetrainStep";
import { PortsStep } from "./PortsStep";
import { ControlsStep } from "./ControlsStep";
import { AttachmentsStep } from "./AttachmentsStep";
import { ReviewStep } from "./ReviewStep";
import { WizardSteps } from "./WizardSteps";
import { HubReadyFlow } from "./ready/HubReadyFlow";

const STEPS = [
  { short: "Drivetrain", title: "How is your robot built?", hint: "Pick how the motors and wheels are laid out. Hover over a robot to watch it drive." },
  { short: "Ports", title: "Which port is each motor in?", hint: "Match each motor to its hub port. Switch on Reverse if a wheel turns the wrong way." },
  { short: "Controls", title: "How do you want to drive?", hint: "Choose which Xbox stick does what." },
  { short: "Attachments", title: "Add attachments (optional)", hint: "Claws, lifts and arms, run by triggers, bumpers, face buttons or the D-pad." },
  { short: "Finish", title: "Check your setup", hint: "You can change any of this later with Edit setup." },
];
const CONTROLS_STEP = 2;
const ATTACH_STEP = 3;
const clone = (c) => JSON.parse(JSON.stringify(c));

// First step that has a problem the user must fix before moving on.
const firstBlockedStep = (cfg) => {
  if (!cfg.drivetrain) return 0;
  const drive = Object.values(cfg.motors).map((m) => m.port);
  if (new Set(drive).size !== drive.length) return 1;
  if (!cfg.style) return CONTROLS_STEP;
  if (portConflicts(cfg).size || buttonConflicts(cfg).size) return ATTACH_STEP;
  return STEPS.length;
};

export const RcWizard = ({ open, initial, editing = false, askName = false, onClose, onFinish }) => {
  const [cfg, setCfg] = useState(blankConfig);
  const [phase, setPhase] = useState("setup");
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [step]);

  useEffect(() => {
    if (!open) return;
    const start = initial ? { ...defaultConfig(initial.drivetrain), ...clone(initial) } : blankConfig();
    setCfg(start);
    setPhase(!editing && !hubReady() ? "ready" : "setup");
    setStep(0);
    setReached(editing ? STEPS.length - 1 : 0);
    setName(initial ? rcProjectName(start) : "");
    setNameTouched(false);
    setSaving(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const blockedAt = firstBlockedStep(cfg);
  const canGo = (i) => i <= reached && i <= blockedAt;
  const go = (i) => { setStep(i); setReached((r) => Math.max(r, i)); };
  const last = step === STEPS.length - 1;

  const pickDrivetrain = (id) => {
    if (id === cfg.drivetrain) return;
    const base = defaultConfig(id);
    const style = STYLES_BY_KIND[drivetrainById(id).kind].includes(cfg.style) ? cfg.style : null;
    const next = { ...base, style, attachments: fitAttachments(base, cfg.attachments) };
    if (next.attachments.length < cfg.attachments.length) toast.info("Not enough free ports for every attachment, so the last ones were removed");
    setCfg(next);
    if (!nameTouched) setName(rcProjectName(next));
  };

  const finish = async () => {
    setSaving(true);
    try {
      await onFinish(cfg, name.trim() || rcProjectName(cfg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-3xl p-0 sm:rounded-3xl"
        data-testid="rc-wizard-dialog"
      >
        {phase !== "setup" ? (
          <HubReadyFlow
            onReady={() => setPhase("setup")}
            onSkip={() => setPhase("setup")}
            onCancel={phase === "help" ? () => setPhase("setup") : onClose}
            cancelLabel={phase === "help" ? "Back to setup" : "Cancel"}
          />
        ) : (<>
        <div className="relative border-b px-5 pb-5 pt-6 sm:px-7">
          <button type="button" onClick={() => setPhase("help")} className="absolute right-14 top-5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" data-testid="rc-hub-help-button">
            <LifeBuoy className="h-3.5 w-3.5" /><span className="hidden sm:inline">Hub setup help</span>
          </button>
          <p className="eyebrow pr-8 text-accent dark:text-primary" data-testid="rc-wizard-eyebrow"><span className="hidden sm:inline">Easy Remote Control Setup · </span>Step {step + 1} of {STEPS.length}</p>
          <DialogTitle className="mt-2 font-display text-lg font-extrabold tracking-tight" data-testid="rc-wizard-title">{STEPS[step].title}</DialogTitle>
          <DialogDescription className="mt-1">{STEPS[step].hint}</DialogDescription>
          <WizardSteps steps={STEPS} step={step} canGo={canGo} onGo={go} />
        </div>
        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-7" data-testid="rc-wizard-body">
          {step === 0 && <DrivetrainStep value={cfg.drivetrain} onPick={pickDrivetrain} />}
          {step === 1 && <PortsStep cfg={cfg} setCfg={setCfg} />}
          {step === CONTROLS_STEP && <ControlsStep cfg={cfg} onPick={(style) => setCfg((c) => ({ ...c, style }))} />}
          {step === ATTACH_STEP && <AttachmentsStep cfg={cfg} setCfg={setCfg} />}
          {last && <ReviewStep cfg={cfg} askName={askName} name={name} onName={(v) => { setName(v); setNameTouched(true); }} />}
        </div>
        <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-5 py-4 sm:px-7">
          <Button variant="ghost" className="rounded-full" onClick={() => (step ? go(step - 1) : onClose())} disabled={saving} data-testid="rc-wizard-back">
            <ArrowLeft className="mr-1.5 h-4 w-4" />{step ? "Back" : "Cancel"}
          </Button>
          {last ? (
            <Button onClick={finish} disabled={saving || (askName && !name.trim())} className="rounded-full bg-accent px-6 text-accent-foreground hover:bg-accent/90" data-testid="rc-wizard-finish">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1.5 h-4 w-4" />}
              <span className="sm:hidden">{editing ? "Save" : "Create"}</span>
              <span className="hidden sm:inline">{editing ? "Save setup" : "Create project"}</span>
            </Button>
          ) : (
            <Button onClick={() => go(step + 1)} disabled={step >= blockedAt} className="rounded-full px-6" data-testid="rc-wizard-next">
              <span className="sm:hidden">Next</span><span className="hidden sm:inline">Next: {STEPS[step + 1].short}</span><ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
